/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Breadcrumb } from 'antd';
import IconButton from '~components/_common/IconButton/IconButton';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useStyles } from '~src/hooks/useStyles';

import FileTenderHammerSrc from '~src/assets/nav-icons/file-tender-hammer.svg';
// Primary-/Secondary Share Keys: plain (no hammer) icon for a Secondary-Share-Key
// tender breadcrumb, matching the differentiation already applied in HomeLeftSideView.tsx.
import FileTenderSrc from '~src/assets/nav-icons/file-tender.svg';
import FolderTenderHammer from '~src/assets/nav-icons/folder-tender-hammer.svg';
import FolderTender from '~src/assets/nav-icons/folder-tender.svg';
import ModuleTenderHammer from '~src/assets/nav-icons/module-tender-hammer-color.svg';
import ProjectSrc from '~src/assets/nav-icons/project.png';
import { useShares } from '~src/hooks/api-hooks/useShares';
import { useSharedTenderSubfolders } from '~src/hooks/api-hooks/useSharedTenderFoldersFolders';
import { useSharedTenderFolders } from '~src/hooks/api-hooks/useSharedTenderFolders';
import { DbTenderFolder } from '~src/types/db';
import {
  BaseHomeNavigationKeys,
  navigationItemsHome,
} from '~src/data/navigationItems';
import { StateBreadCrumb } from '../Breadcrumbs';

import { styleFn } from '../style';

const MyTendersBreadcrumbs = () => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const location = useLocation();

  const paths = useMemo(() => location.pathname.split('/').filter(Boolean), [
    location.pathname,
  ]);

  // New URL format:
  // /my-tenders/:shareIdKey/...
  const shareIdKey = paths[1];

  const { shareId, shareKey } = useMemo(() => {
    const raw = String(shareIdKey || '');
    const parts = raw.split('-');
    return {
      shareId: parts[0] || '',
      shareKey: parts.slice(1).join('-') || '',
    };
  }, [shareIdKey]);

  // Robust folder ids parsing (no magic indexes)
  const tenderFoldersIndex = paths.indexOf('tender-folders');
  const foldersIndex = paths.indexOf('folders');

  const rootFolderId =
    tenderFoldersIndex !== -1 ? paths[tenderFoldersIndex + 1] : undefined;
  const currentFolderId =
    foldersIndex !== -1 ? paths[foldersIndex + 1] : undefined;

  const isSubfolderLevel =
    !!currentFolderId && !!rootFolderId && rootFolderId !== currentFolderId;

  const containerRef = useRef<HTMLDivElement>(null);

  const [breadcrumbs, setBreadcrumbs] = useState<StateBreadCrumb[]>([]);

  const shareTendersQuery = useShares(
    {},
    {
      refetchOnMount: false,
      // enabled: !!shareId
    }
  );

  const sharedFoldersQueryResult = useSharedTenderFolders(
    {
      id: shareId,
      key: shareKey,
    },
    {
      enabled: !!shareId && !!shareKey && !!currentFolderId,
      refetchOnMount: false,
    }
  );

  const sharedSubfoldersQueryResult = useSharedTenderSubfolders(
    { root_folder_id: rootFolderId, share_key: shareKey, share_id: shareId },
    {
      enabled: !!shareId && !!shareKey && !!isSubfolderLevel,
      refetchOnMount: false,
    }
  );

  useEffect(() => {
    const nextBreadcrumbs: StateBreadCrumb[] = [];

    const myTendersNavItem = navigationItemsHome.find(
      ({ key }) => key === BaseHomeNavigationKeys.MY_TENDERS
    );

    nextBreadcrumbs.push({
      name: intl.formatMessage({ id: 'my-tender' }),
      path: '/my-tenders',
      iconKey: 'ModuleTenderHammer',
      navItem: {
        customIcon: myTendersNavItem?.customIcon,
      },
    });

    // Tender breadcrumb (share instance)
    if (shareIdKey && shareTendersQuery.data) {
      const shareTender = shareTendersQuery.data.find(
        ({ share_id, key }) =>
          String(share_id) === String(shareId) &&
          String(key?.share_key_id ?? '') === String(shareKey ?? '')
      );

      const displayName =
        // shareTender?.share_title ||
        // shareTender?.key?.share_key_title ||
        shareTender?.registration?.registration_title ||
        // shareTender?.project?.name ||
        '';

      const isSecondaryShareKey =
        shareTender?.key?.share_key_is_primary === false;

      nextBreadcrumbs.push({
        name: displayName,
        path: `/my-tenders/${shareIdKey}`,
        iconKey: isSecondaryShareKey ? 'FileTenderSrc' : 'FileTenderHammerSrc',
      });
    }

    // Only build folder breadcrumbs on documents routes
    if (paths[paths.length - 1] !== 'documents') {
      setBreadcrumbs(nextBreadcrumbs);
      sessionStorage.setItem('breadcrumbPath', JSON.stringify(nextBreadcrumbs));
      return;
    }

    if (rootFolderId && sharedFoldersQueryResult.data) {
      const rootFolder = sharedFoldersQueryResult.data.find(
        ({ id }) => String(id) === String(rootFolderId)
      );

      nextBreadcrumbs.push({
        name: rootFolder?.alias_title,
        path: `/my-tenders/${shareIdKey}/tender-folders/${rootFolderId}/folders/${rootFolderId}/documents`,
        iconKey: 'FolderTenderHammer',
      });

      if (isSubfolderLevel && sharedSubfoldersQueryResult.data) {
        nextBreadcrumbs.push(
          ...getSubfolderBreadcrumbsList({
            foldersList: sharedSubfoldersQueryResult.data,
            currentFolderId: String(currentFolderId),
            rootFolderId: String(rootFolderId),
            shareIdKey: String(shareIdKey),
            subFoldersBreadCrumpsList: [],
          })
        );
      }
    }

    setBreadcrumbs(nextBreadcrumbs);
    sessionStorage.setItem('breadcrumbPath', JSON.stringify(nextBreadcrumbs));
  }, [
    paths,
    intl,
    shareId,
    shareKey,
    shareIdKey,
    shareTendersQuery.data,
    sharedFoldersQueryResult.data,
    sharedSubfoldersQueryResult.data,
    rootFolderId,
    currentFolderId,
    isSubfolderLevel,
  ]);

  return (
    <div css={style.component}>
      <div ref={containerRef} css={style.container}>
        {!!breadcrumbs[0]?.navItem?.icon &&
        !breadcrumbs[0]?.navItem.customIcon ? (
          <IconButton
            icon={breadcrumbs[0].navItem.icon as IconProp}
            color={breadcrumbs[0].navItem.color}
            customStyle={style.icons}
            backgroundOff
          />
        ) : (
          <NavIcon
            customStyle={Object.assign(
              {},
              typeof style.navIcon === 'object' ? style.navIcon : {},
              {
                width: breadcrumbs[0]?.path === '/my-tenders' ? '38px' : 'auto',
              }
            )}
            icon={breadcrumbs[0]?.navItem?.customIcon || ''}
          />
        )}
        {breadcrumbs?.length === 0 && (
          <NavIcon customStyle={style.navIcon} icon={ProjectSrc} />
        )}
        <Breadcrumb css={{ whiteSpace: 'nowrap' }} separator=">">
          {breadcrumbs?.length === 0 && (
            <Breadcrumb.Item css={{ display: 'inline-block' }} key={`project`}>
              <Link to={`/`}>{intl.formatMessage({ id: 'home' })}</Link>
            </Breadcrumb.Item>
          )}
          {breadcrumbs.map((item) => {
            const icons = {
              ModuleTenderHammer,
              FolderTender,
              FolderTenderHammer,
              FileTenderHammerSrc,
              FileTenderSrc,
            };
            return !item.disabled ? (
              <Breadcrumb.Item
                css={{ display: 'inline-block' }}
                key={`${item.path}-${item.name}`}>
                {item === breadcrumbs[breadcrumbs.length - 1] &&
                  item !== breadcrumbs[0] && (
                    <NavIcon
                      customStyle={style.navIcon}
                      icon={
                        icons[
                          breadcrumbs[breadcrumbs.length - 1]?.iconKey ||
                            'FolderTender'
                        ]
                      }
                    />
                  )}
                <Link to={item.path}>{item.name}</Link>
              </Breadcrumb.Item>
            ) : (
              <Breadcrumb.Item key={`${item.path}-${item.name}`}>
                {item.name}
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
        <div css={{ minWidth: 10 }}></div>
      </div>
    </div>
  );
};

function getSubfolderBreadcrumbsList({
  foldersList,
  currentFolderId,
  rootFolderId,
  shareIdKey,
  subFoldersBreadCrumpsList,
}: {
  foldersList: DbTenderFolder[];
  currentFolderId: string;
  rootFolderId: string;
  shareIdKey: string;
  subFoldersBreadCrumpsList: StateBreadCrumb[];
}) {
  // 🔒 safety: prevent infinite recursion
  if (!currentFolderId) return subFoldersBreadCrumpsList;

  const currentFolder = foldersList.find(
    ({ id }) => String(id) === String(currentFolderId)
  );

  // 🔒 safety: folder not found in list
  if (!currentFolder) {
    return subFoldersBreadCrumpsList;
  }

  subFoldersBreadCrumpsList.unshift({
    name: currentFolder.name,
    path: `/my-tenders/${shareIdKey}/tender-folders/${rootFolderId}/folders/${currentFolderId}/documents`,
    iconKey: 'FolderTender',
  });

  const parentId = currentFolder.parent_id;

  // 🛑 STOP CONDITIONS
  if (
    parentId === null ||
    parentId === undefined ||
    String(parentId) === String(rootFolderId) ||
    String(parentId) === String(currentFolderId)
  ) {
    return subFoldersBreadCrumpsList;
  }

  return getSubfolderBreadcrumbsList({
    foldersList,
    currentFolderId: String(parentId),
    rootFolderId,
    shareIdKey,
    subFoldersBreadCrumpsList,
  });
}

export default MyTendersBreadcrumbs;
